import React, { useEffect, useState } from "react";
import { getCdpPersonProfile, getCdpPersonJourney } from "../../api";
import { titleCase } from "../shared/format";

/* ───────────── helpers ───────────── */
function fmtDate(ts) { if (!ts) return ""; const d = new Date(ts); return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`; }
function fmtTime(ts) { if (!ts) return ""; return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true }); }
function relativeTime(ts) { if (!ts) return ""; const s = (Date.now() - new Date(ts).getTime()) / 1000; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }
function stripDomain(url) { if (!url) return ""; try { return new URL(url).pathname; } catch { return url; } }
function truncate(s, m = 45) { return !s || s.length <= m ? s || "" : s.slice(0, m) + "\u2026"; }
function fmtDollar(v) { return v != null ? `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "\u2014"; }

const STAGE_BADGE = { known: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", booked: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", repeat: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" };
const CH_PILL = { "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300", "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" };
const EVT_DOT = { page_view: "bg-blue-500", book_now_click: "bg-emerald-500", booking_completed: "bg-amber-500", email_capture: "bg-purple-500", phone_click: "bg-blue-500", video_play: "bg-teal-500", form_submit: "bg-blue-400", outbound_click: "bg-gray-400", exit_intent: "bg-red-400", booking_hover: "bg-yellow-400" };
const EVT_LABEL = { page_view: "Viewed", book_now_click: "Clicked Book Now", booking_completed: "Booking Completed", email_capture: "Email captured", phone_click: "Clicked phone", video_play: "Played video", form_submit: "Submitted form", outbound_click: "External link", exit_intent: "Exit intent" };
const SKIP = new Set(["perf_metrics", "visibility_change"]);

/**
 * Shared Person Profile slide-out.
 * @param {string} clientId
 * @param {string|null} personId
 * @param {string|null} cvId
 * @param {function} onClose
 */
export default function PersonProfile({ clientId, personId, cvId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journeyLoading, setJourneyLoading] = useState(false);

  useEffect(() => {
    if (!clientId || (!personId && !cvId)) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await getCdpPersonProfile(clientId, { personId, cvId });
        if (!cancelled) {
          setProfile(resp?.data || null);
          // Load journey if person resolved
          const pid = resp?.data?.person?.person_id;
          if (pid) {
            setJourneyLoading(true);
            const jResp = await getCdpPersonJourney(clientId, pid);
            if (!cancelled) setJourney(jResp?.data || null);
            setJourneyLoading(false);
          }
        }
      } catch (e) { console.error("Person profile error:", e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, personId, cvId]);

  const p = profile?.person;
  const vis = profile?.vis_score ?? 0;
  // Identifiers shown in the slide-out. Internal tracking IDs (cv_id,
  // ga_client_id) are never displayed — they're implementation details, not
  // something a reader recognizes. The backend occasionally returns the same
  // id_type+id_value pair twice (e.g. a visitor matched across two sessions),
  // so collapse exact duplicates to keep the list one-per-identifier.
  const idents = (profile?.identifiers || [])
    .filter(i => i && i.id_type && !["cv_id", "ga_client_id"].includes(i.id_type))
    .filter((i, idx, arr) =>
      arr.findIndex(j => j.id_type === i.id_type && j.id_value === i.id_value) === idx
    );
  const dc = profile?.decision_cycle;
  const stats = profile?.stats || {};
  const events = journey?.events || [];
  const bookings = journey?.bookings || [];
  const emailEvents = journey?.email_events || [];

  // Group events by session
  const sessionGroups = {};
  const standalone = [];
  for (const e of events) {
    if (SKIP.has(e.event_type)) continue;
    if (e.session_id) {
      if (!sessionGroups[e.session_id]) sessionGroups[e.session_id] = [];
      sessionGroups[e.session_id].push(e);
    } else {
      standalone.push(e);
    }
  }

  // VIS color
  const visColor = vis >= 70 ? "text-emerald-500" : vis >= 40 ? "text-amber-500" : "text-gray-400";
  const visBg = vis >= 70 ? "bg-emerald-500" : vis >= 40 ? "bg-amber-500" : "bg-gray-300 dark:bg-gray-600";

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      <div className="fixed inset-0 bg-black/20 dark:bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-[560px] bg-white dark:bg-gray-900 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-gray-800">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-400">Loading profile...</div>
        ) : !p ? (
          <div className="p-6">
            <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 mb-4">&larr; Back</button>
            <div className="text-center py-12 text-gray-400">
              {profile?.anonymous ? `Anonymous visitor (${cvId?.slice(0, 8)}...)` : "Person not found"}
            </div>
          </div>
        ) : (
          <>
            {/* ─── Header ─── */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-5 py-4 z-10">
              <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 mb-3">&larr; Back</button>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-400 text-lg font-bold shrink-0">
                  {(p.first_name || p.primary_email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{[titleCase(p.first_name), titleCase(p.last_name)].filter(Boolean).join(" ") || p.primary_email || `Person #${p.person_id}`}</div>
                  {p.primary_email && <div className="text-xs text-gray-500 dark:text-gray-400">{p.primary_email}</div>}
                  {p.primary_phone && <div className="text-xs text-gray-500 dark:text-gray-400">{p.primary_phone}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    {p.lifecycle_stage && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STAGE_BADGE[p.lifecycle_stage] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>{p.lifecycle_stage}</span>}
                    {p.source && <span className="text-[10px] text-gray-400 dark:text-gray-500">Source: {p.source}</span>}
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {[
                  { label: "Sessions", value: stats.sessions || p.booking_count || 0 },
                  { label: "Bookings", value: p.booking_count || 0 },
                  { label: "Lifetime", value: fmtDollar(p.total_booking_value) },
                  { label: "Devices", value: stats.devices || 1 },
                  { label: "Channels", value: idents.length || "\u2014" },
                  { label: "First Seen", value: fmtDate(p.created_at) },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{s.value}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* VIS Score */}
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-500 dark:text-gray-400">Intent Score</span>
                  <span className={`font-bold ${visColor}`}>{vis}/100</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${visBg}`} style={{ width: `${vis}%` }}></div>
                </div>
              </div>

              {/* Decision Cycle */}
              {dc && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Took {dc.hours_to_book < 24 ? `${dc.hours_to_book}h` : `${Math.round(dc.hours_to_book / 24 * 10) / 10} days`} and {dc.sessions_to_book} session{dc.sessions_to_book !== 1 ? "s" : ""} to book
                </div>
              )}
            </div>

            {/* ─── Identifiers ─── */}
            {idents.length > 0 && (
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Identifiers</h4>
                <div className="space-y-1">
                  {idents.map((id, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400 font-mono">{id.id_type}</span>
                      <span className="text-gray-700 dark:text-gray-300 font-mono">{id.id_value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Journey Timeline ─── */}
            <div className="px-5 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Journey</h4>

              {journeyLoading ? (
                <div className="text-center py-6 text-gray-400 text-sm">Loading journey...</div>
              ) : (
                <div className="space-y-4">
                  {/* Booking cards interleaved */}
                  {bookings.map((b, i) => (
                    <div key={`bk-${i}`} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                      <div className="text-xs font-semibold text-amber-700 dark:text-amber-400">Booking — {b.deepest_step}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {b.check_in_date && `${fmtDate(b.check_in_date)} \u2192 ${fmtDate(b.check_out_date)}`}
                        {b.num_nights && ` \u00b7 ${b.num_nights} nights`}
                        {b.site_type_name && ` \u00b7 ${b.site_type_name}`}
                      </div>
                      {b.total != null && <div className="text-xs font-medium text-amber-800 dark:text-amber-300 mt-0.5">{fmtDollar(b.total)}</div>}
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{fmtDate(b.created_at)}</div>
                    </div>
                  ))}

                  {/* Email events */}
                  {emailEvents.map((e, i) => (
                    <div key={`em-${i}`} className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg px-4 py-2">
                      <div className="text-xs text-pink-700 dark:text-pink-400">{e.event_type === "email_open" ? "Opened email" : "Clicked link in email"}</div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500">{fmtDate(e.event_timestamp)}</div>
                    </div>
                  ))}

                  {/* Session groups */}
                  {Object.entries(sessionGroups).map(([sid, evts]) => {
                    const first = evts[0];
                    const last = evts[evts.length - 1];
                    const channel = evts.find(e => e.traffic_channel_group)?.traffic_channel_group;
                    const device = evts.find(e => e.device_type)?.device_type;
                    const city = evts.find(e => e.geo_city)?.geo_city;
                    const hbCount = evts.filter(e => e.event_type === "heartbeat").length;
                    const displayEvts = [];
                    let hbGroup = null;
                    for (const e of evts) {
                      if (SKIP.has(e.event_type)) continue;
                      if (e.event_type === "heartbeat") { hbGroup = (hbGroup || 0) + 1; continue; }
                      if (hbGroup) { displayEvts.push({ type: "hb", count: hbGroup }); hbGroup = null; }
                      displayEvts.push(e);
                    }
                    if (hbGroup) displayEvts.push({ type: "hb", count: hbGroup });

                    return (
                      <div key={sid} className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span>{fmtDate(first?.event_timestamp)}</span>
                            {channel && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${CH_PILL[channel] || CH_PILL["Direct"]}`}>{channel}</span>}
                            {device && <span>{device === "mobile" ? "\ud83d\udcf1" : "\ud83d\udcbb"}</span>}
                            {city && <span>{city}</span>}
                          </div>
                          {hbCount > 0 && <span className="text-gray-400 dark:text-gray-500">~{Math.round(hbCount * 30 / 60)}m</span>}
                        </div>
                        <div className="px-4 py-2 space-y-1">
                          {displayEvts.slice(0, 20).map((e, i) => {
                            if (e.type === "hb") return <div key={`hb-${i}`} className="text-[10px] text-gray-400 dark:text-gray-500 italic pl-4">Active for ~{e.count * 30}s</div>;
                            const dot = EVT_DOT[e.event_type] || "bg-gray-400";
                            const label = EVT_LABEL[e.event_type] || e.event_type;
                            const bold = ["book_now_click", "booking_completed"].includes(e.event_type);
                            const pageInfo = e.event_type === "page_view" ? `: ${truncate(e.page_title || stripDomain(e.page_url))}` : e.page_url ? ` \u2192 ${truncate(stripDomain(e.page_url), 30)}` : "";
                            return (
                              <div key={i} className="flex items-start gap-2">
                                <div className={`w-2 h-2 rounded-full ${dot} mt-1 shrink-0`}></div>
                                <div className={`text-xs ${bold ? "font-semibold text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400"} flex-1`}>{label}{pageInfo}</div>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">{fmtTime(e.event_timestamp)}</span>
                              </div>
                            );
                          })}
                          {displayEvts.length > 20 && <div className="text-[10px] text-gray-400 text-center">+{displayEvts.length - 20} more events</div>}
                        </div>
                      </div>
                    );
                  })}

                  {Object.keys(sessionGroups).length === 0 && bookings.length === 0 && emailEvents.length === 0 && (
                    <div className="text-center py-6 text-gray-400 text-sm">No journey data</div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
